import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminActivityBadgeCertification.css";

type OfficialBadge={
  badgeKey:string;
  category:string;
  studentName:string;
  teacherName:string;
  description:string;
  minScore:number;
  credentialDefinitionId:string;
  externalProfile:string;
  issuerName:string;
};

type CertificationRequest={
  requestId:string;
  status:string;
  version:number;
  requestedAt:string;
  requestNote?:string|null;
  decisionReason?:string|null;
  targetBadgeKey?:string|null;
  reviewScore?:number|null;
  learner:{learnerId:string;displayName:string};
  badge:{
    badgeId:string;
    title:string;
    description?:string|null;
    activityType:string;
    activityName:string;
    activityOn?:string|null;
    iconKey:string;
    themeKey:string;
    selfIssuedAt:string;
  };
  sources:Array<{sourceType:string;sourceId:string;label:string}>;
  verifiedEvidence:Array<{evidenceId:string;title:string;type:string;verifiedAt?:string|null}>;
  credentialRequestId?:string|null;
  officialCredential?:{
    credentialId:string;
    status:string;
    credentialName:string;
    verificationUri?:string|null;
    issuedAt:string;
  }|null;
};

type StudioData={officialBadges:OfficialBadge[];requests:CertificationRequest[]};

function formatDate(value?:string|null){
  if(!value)return "-";
  const d=new Date(value);
  if(Number.isNaN(d.getTime()))return value;
  return d.toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}

function statusLabel(status:string){
  const map:Record<string,string>={
    REQUESTED:"심사 요청",
    APPROVED:"승인·발급 진행",
    REJECTED:"반려",
    CANCELLED:"학생 취소"
  };
  return map[status]||status;
}

export function AdminActivityBadgeCertification({client}:{client:PlatformClient}){
  const [data,setData]=useState<StudioData|null>(null);
  const [filter,setFilter]=useState("REQUESTED");
  const [selectedId,setSelectedId]=useState("");
  const [badgeKey,setBadgeKey]=useState("");
  const [evidenceId,setEvidenceId]=useState("");
  const [score,setScore]=useState(70);
  const [reason,setReason]=useState("활동 근거와 공식 배지 기준을 확인했습니다.");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_activity_badge_certification_studio",{
      p_status:filter==="ALL"?null:filter
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as StudioData;
      setData(next);
      setSelectedId((current)=>next.requests.some((item)=>item.requestId===current)?current:(next.requests[0]?.requestId||""));
      setError("");
    });
    return()=>{active=false;};
  },[client,filter,refresh]);

  const selected=useMemo(()=>data?.requests.find((item)=>item.requestId===selectedId)||null,[data,selectedId]);
  const selectedOfficial=useMemo(()=>data?.officialBadges.find((item)=>item.badgeKey===badgeKey)||null,[data,badgeKey]);

  useEffect(()=>{
    if(!data||!selected)return;
    const requestedBadge=selected.targetBadgeKey&&data.officialBadges.some((item)=>item.badgeKey===selected.targetBadgeKey)
      ? selected.targetBadgeKey
      : data.officialBadges[0]?.badgeKey||"";
    setBadgeKey(requestedBadge);
    const official=data.officialBadges.find((item)=>item.badgeKey===requestedBadge);
    setScore(Number(selected.reviewScore??official?.minScore??70));
    setEvidenceId(selected.verifiedEvidence[0]?.evidenceId||"");
    setReason(selected.decisionReason||"활동 근거와 공식 배지 기준을 확인했습니다.");
  },[data,selected]);

  async function triggerIssuer(requestId:string){
    const {data,error}=await client.auth.getSession();
    if(error||!data.session?.access_token)throw new Error("로그인 세션을 확인할 수 없습니다.");
    const response=await fetch("/issuer/process",{
      method:"POST",
      headers:{"content-type":"application/json","authorization":"Bearer "+data.session.access_token},
      body:JSON.stringify({requestId})
    });
    const body=await response.json().catch(()=>({}));
    if(!response.ok)throw new Error(body?.message||"Issuer 처리를 시작하지 못했습니다.");
  }

  async function approve(){
    if(!selected||!badgeKey||!evidenceId)return;
    setBusy("approve");setError("");setNotice("");
    const {data,error}=await client.rpc("admin_decide_activity_badge_certification",{
      p_request_id:selected.requestId,
      p_expected_version:selected.version,
      p_decision:"APPROVE",
      p_badge_key:badgeKey,
      p_evidence_id:evidenceId,
      p_score:score,
      p_reason:reason.trim()
    });
    if(error){setError(error.message);setBusy("");return;}
    const result=data as {credentialRequestId?:string};
    if(result.credentialRequestId){
      try{
        await triggerIssuer(result.credentialRequestId);
        setNotice("본사 인증 승인과 Open Badges 3.0 발급 처리를 시작했습니다.");
      }catch(err){
        setNotice("본사 인증은 승인됐습니다. Issuer 처리는 자격증명 큐에서 이어서 실행하세요.");
      }
    }
    setRefresh((v)=>v+1);
    setBusy("");
  }

  async function reject(){
    if(!selected)return;
    setBusy("reject");setError("");setNotice("");
    const {error}=await client.rpc("admin_decide_activity_badge_certification",{
      p_request_id:selected.requestId,
      p_expected_version:selected.version,
      p_decision:"REJECT",
      p_badge_key:null,
      p_evidence_id:null,
      p_score:null,
      p_reason:reason.trim()
    });
    if(error){setError(error.message);setBusy("");return;}
    setNotice("학생 활동배지 인증 요청을 반려했습니다.");
    setRefresh((v)=>v+1);
    setBusy("");
  }

  return (
    <section className="admin-activity-cert">
      <div className="admin-activity-cert-hero">
        <div>
          <p className="eyebrow">HQ CERTIFICATION</p>
          <h2>학생 활동배지 본사 인증</h2>
          <p>학생 자가발급 배지를 그대로 국제표준으로 인정하지 않습니다. 검증 증빙과 공식 배지 판정 기준을 통과한 경우에만 Open Badges 3.0 Life Stamp로 승격합니다.</p>
        </div>
        <span className="phase-badge">PLATFORM_ADMIN</span>
      </div>

      {error&&<p className="error">{error}</p>}
      {notice&&<p className="admin-activity-cert-notice">{notice}</p>}

      <div className="admin-activity-cert-filter">
        {["REQUESTED","APPROVED","REJECTED","ALL"].map((value)=>(
          <button key={value} className={filter===value?"active":""} onClick={()=>setFilter(value)}>{value==="ALL"?"전체":statusLabel(value)}</button>
        ))}
      </div>

      <div className="admin-activity-cert-layout">
        <aside className="admin-activity-cert-list">
          {data?.requests.map((item)=>(
            <button key={item.requestId} className={selectedId===item.requestId?"active":""} onClick={()=>setSelectedId(item.requestId)}>
              <span>{statusLabel(item.status)} · {item.learner.displayName}</span>
              <strong>{item.badge.title}</strong>
              <small>{item.badge.activityName} · {formatDate(item.requestedAt)}</small>
            </button>
          ))}
          {!data?.requests.length&&<p className="case-empty">해당 상태의 인증 요청이 없습니다.</p>}
        </aside>

        <div className="admin-activity-cert-detail">
          {selected ? (
            <>
              <div className="admin-activity-cert-badge">
                <span>{selected.badge.iconKey}</span>
                <div><p className="eyebrow">학생 활동배지 · 비표준</p><h3>{selected.badge.title}</h3><strong>{selected.badge.activityName}</strong><small>{selected.badge.activityType}</small></div>
              </div>

              {selected.badge.description&&<p className="admin-activity-cert-description">{selected.badge.description}</p>}
              {selected.requestNote&&<div className="admin-activity-cert-note"><strong>학생 요청 메모</strong><p>{selected.requestNote}</p></div>}

              <div className="admin-activity-cert-sources">
                <strong>연결 활동</strong>
                <div>{selected.sources.map((source)=><span key={source.sourceType+source.sourceId}>{source.label}</span>)}</div>
              </div>

              <div className="admin-activity-cert-evidence">
                <strong>검증 증빙</strong>
                {selected.verifiedEvidence.length?selected.verifiedEvidence.map((item)=>(
                  <label key={item.evidenceId}>
                    <input type="radio" name="certEvidence" checked={evidenceId===item.evidenceId} onChange={()=>setEvidenceId(item.evidenceId)} disabled={selected.status!=="REQUESTED"} />
                    <div><strong>{item.title}</strong><span>{item.type} · {formatDate(item.verifiedAt)}</span></div>
                  </label>
                )):<p className="case-empty">공식 승격에 사용할 검증 완료 증빙이 없습니다.</p>}
              </div>

              {selected.status==="REQUESTED" ? (
                <div className="admin-activity-cert-decision">
                  <label>공식 배지
                    <select value={badgeKey} onChange={(e)=>{
                      const next=e.target.value;
                      setBadgeKey(next);
                      const official=data?.officialBadges.find((item)=>item.badgeKey===next);
                      if(official)setScore(Number(official.minScore));
                    }}>
                      {data?.officialBadges.map((item)=><option value={item.badgeKey} key={item.badgeKey}>{item.studentName} · 최소 {item.minScore}점</option>)}
                    </select>
                  </label>

                  {selectedOfficial&&<div className="admin-activity-cert-standard"><strong>{selectedOfficial.studentName}</strong><span>{selectedOfficial.issuerName} · {selectedOfficial.externalProfile} · 최소 {selectedOfficial.minScore}점</span><p>{selectedOfficial.description}</p></div>}

                  <label>본사 심사점수<input type="number" min={0} max={100} value={score} onChange={(e)=>setScore(Number(e.target.value))} /></label>
                  <label>심사 의견<textarea value={reason} onChange={(e)=>setReason(e.target.value)} maxLength={2000} /></label>

                  <div className="admin-activity-cert-actions">
                    <button className="primary" disabled={busy!==""||!evidenceId||!badgeKey||!reason.trim()} onClick={approve}>기준 확인 · 공식 승격 승인</button>
                    <button className="secondary" disabled={busy!==""||!reason.trim()} onClick={reject}>반려</button>
                  </div>
                </div>
              ) : (
                <div className="admin-activity-cert-result">
                  <strong>{statusLabel(selected.status)}</strong>
                  <span>{selected.decisionReason||"심사 기록 없음"}</span>
                  {selected.officialCredential&&<a href={selected.officialCredential.verificationUri||"#"} target="_blank" rel="noreferrer">{selected.officialCredential.credentialName} 공개 검증</a>}
                </div>
              )}
            </>
          ):<p className="case-empty">인증 요청을 선택하세요.</p>}
        </div>
      </div>
    </section>
  );
}
